import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { getMyProfile } from "../../actions/user";
import { Box, Button, Form, Heading, Image } from "../../components/elements";
import { IconField } from "../../components/fields/IconField";
import { Txt } from "../../components/generalComponets";
import { Logo } from "../../components/Logo";
import { AppPreloader } from "../../context/Preloader";
import { AuthenticationService } from "../../services/auth.service";
import { LOGO_ICON, PATTERN } from "../../utils/image.constants";
import { APP_NAME } from "../../utils/app.constants";
import { MODULE_ROUTE_MAP, sanitizeReturnPath } from "../../utils/authUtils";
import { clearTableState } from "../../utils/tableStateStorage";

// Simple config for logo and pattern
const LOGO_ALT = "blue";
const PATTERN_ALT = "line-pattern";
const TITLE = `Login to ${APP_NAME}`;

// ids used to focus fields without depending on IconField forwarding refs
const USERNAME_ID = "login-username-input";
const PASSWORD_ID = "login-password-input";

export const LoginScreen = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [checkingSession, setCheckingSession] = useState(true);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const getReturnToFromHash = () => {
    const hash = window.location.hash || "";
    const queryIndex = hash.indexOf("?");
    if (queryIndex === -1) return null;
    const queryString = hash.slice(queryIndex + 1);
    const params = new URLSearchParams(queryString);
    return params.get("returnTo");
  };

  const getPreferredRedirectRoute = (profile = {}) => {
    const returnTo = sanitizeReturnPath(getReturnToFromHash());
    if (returnTo) {
      return returnTo;
    }

    if (profile?.type === "SUPER_ADMIN") {
      return "/companies";
    }

    return getFirstAccessibleRoute(profile?.permissions);
  };

  // Check token & redirect
  useEffect(() => {
    checkUserToken();
  }, []);

  const getFirstAccessibleRoute = (permissions = []) => {
    if (!Array.isArray(permissions) || permissions.length === 0) return "/dashboard";

    // Find the first route the user has access to based on their permissions
    for (const moduleKey of Object.keys(MODULE_ROUTE_MAP)) {
      if (permissions.includes(moduleKey)) {
        return MODULE_ROUTE_MAP[moduleKey];
      }
    }

    return "/dashboard";
  };

  const checkUserToken = async () => {
    try {
      const profileResponse = await dispatch(getMyProfile());
      const response = profileResponse?.data;
      if (!response) {
        return;
      }
      const redirectRoute = getPreferredRedirectRoute(response);
      navigate(redirectRoute, { replace: true });
    } catch {
      // No active session — stay on login page
    } finally {
      setCheckingSession(false);
    }
  };

  // Login handler
  const handleLogin = async () => {
    if (!username || !password) {
      setErrorMsg("Username and Password Required");
      return;
    }

    setErrorMsg("");
    setLoading(true);
    try {
      await AuthenticationService.login({ username, password });
      // A new session never inherits table filters saved in this browser tab by a previous one.
      clearTableState();
      // Cookie is set by the server on successful login
      const profileResponse = await dispatch(getMyProfile());
      const profile = profileResponse?.data;
      if (!profile) {
        throw new Error();
      }

      const redirectRoute = getPreferredRedirectRoute(profile);
      navigate(redirectRoute, { replace: true });
    } catch {
      setErrorMsg("Invalid Username or Password");
    } finally {
      setLoading(false);
    }
  };

  /**
   * @function handleUsernameKeyDown
   * @description Handles the keydown event for the username input field.
   * @param {KeyboardEvent} e - The keydown event.
   */
  const handleUsernameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      document.getElementById(PASSWORD_ID)?.focus();
    }
  };

  /**
   * @function handlePasswordKeyDown
   * @description Handles the keydown event for the password input field.
   * @param {KeyboardEvent} e - The keydown event.
   */
  const handlePasswordKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleLogin();
    }
  };

  if (checkingSession) {
    return <AppPreloader />;
  }

  return (
    <Box className="mc-auth">
      <Image src={PATTERN} alt={PATTERN_ALT} className="mc-auth-pattern" />
      <Box className="mc-auth-group">
        <Logo src={LOGO_ICON} alt={LOGO_ALT} href="/" className="mc-auth-logo" />
        <Heading as="h4" className="mc-auth-title" children={TITLE} />
        <Form className="mc-auth-form" autoComplete="off">
          <IconField
            id={USERNAME_ID}
            icon="email"
            type="text"
            placeholder="Enter your username"
            classes="w-100 h-sm"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={handleUsernameKeyDown}
            autoFocus
          />
          <IconField
            id={PASSWORD_ID}
            icon="lock"
            type="password"
            placeholder="Enter your password"
            passwordVisible={true}
            classes="w-100 h-sm"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handlePasswordKeyDown}
          />
          <Button className="mc-auth-btn h-sm" type="button" disabled={loading} onClick={handleLogin}>
            {loading ? "Signing In..." : "Sign In"}
          </Button>
          {/* <Anchor className="mc-auth-forgot" href={ data?.forgot.path }>{ data?.forgot.text }</Anchor> */}
          <Txt c="r">{errorMsg}</Txt>
        </Form>
      </Box>
    </Box>
  );
};
